import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import type { RootState } from '@/Infrastructure/State';
import { getBuyNowCart } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import type { Cart } from '@/modules/cart/domain/Cart';

export const selectBuyNowCartData = (state: RootState) =>
  state.buyNow.cart.data;
export const selectBuyNowCartLoading = (state: RootState) =>
  state.buyNow.cart.isLoading;
export const selectBuyNowCartError = (state: RootState) =>
  state.buyNow.cart.error;
export const selectBuyNowCartStatusCode = (state: RootState) =>
  state.buyNow.cart.statusCode;
export const selectBuyNowCartRetryCount = (state: RootState) =>
  state.buyNow.cart.retryCount;
export const selectBuyNowCartIsSummaryOpen = (state: RootState) =>
  state.buyNow.isSummaryOpen;
export const selectBuyNowTabActive = (state: RootState) =>
  state.buyNow.tabActive;
export const selectBuyNowIsDatePickerOpen = (state: RootState) =>
  state.buyNow.isDatePickerOpen;
export const selectBuyNowScheduledDate = (state: RootState) =>
  state.buyNow.scheduledDate;
export const selectBuyNowSkuHistory = (state: RootState) =>
  state.buyNow.skuHistory;
export const selectIsProcessingPayment = (state: RootState) =>
  state.buyNow.isProcessingPayment;
export const selectBuyNowSelectedCard = (state: RootState) =>
  state.buyNow.selectedCard;
export const selectBuyNowPaymentError = (state: RootState) =>
  state.buyNow.paymentError;
export const selectBuyNowErrorFallback = (state: RootState) =>
  state.buyNow.isErrorFallback;

const useBuyNowCart = (cartId?: string) => {
  const dispatch = useAppDispatch();
  const data = useAppSelector(selectBuyNowCartData);
  const isLoading = useAppSelector(selectBuyNowCartLoading);
  const error = useAppSelector(selectBuyNowCartError);
  const statusCode = useAppSelector(selectBuyNowCartStatusCode);
  const retryCount = useAppSelector(selectBuyNowCartRetryCount);
  const [isError, setIsError] = useState<boolean>(Boolean(error));
  const hasFetchedRef = useRef<Set<string>>(new Set());

  const getBuyNowCartData = useCallback(
    async (cartId: string) => {
      if (hasFetchedRef.current.has(cartId)) {
        return;
      }
      hasFetchedRef.current.add(cartId);
      const { payload } = await dispatch(getBuyNowCart(cartId));
      if (payload) {
        setIsError(false);
      }
      return payload as Cart;
    },
    [dispatch]
  );

  useEffect(() => {
    if (error) {
      setIsError(true);
    }
  }, [error]);

  useEffect(() => {
    if (cartId && !data && !isLoading && !hasFetchedRef.current.has(cartId)) {
      getBuyNowCartData(cartId);
    }
  }, [cartId, data, getBuyNowCartData, isLoading]);

  return {
    data,
    isLoading,
    error,
    statusCode,
    retryCount,
    isError,
    refetch: getBuyNowCartData
  };
};

export default useBuyNowCart;
